import React, { FC, useEffect, useMemo, useState } from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import cn from "clsx";
import { useHistory } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import { shuffle } from "lodash";
import { useCountryId, useIsRTL, useLongLanguage, useRouteSlug } from "@/hooks";
import { SectionService } from "@/services";
import CatalogService from "@/services/catalogService";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { SECTION_TABS_MODULE_TYPE } from "@/constants/sections";
import DynamicBannersWrapper from "../dynamicBanner";
import { DynamicTabProps, TabData } from "./types";
import {
  getCatalogCarouselData,
  hidePageLoader,
  showPageLoader
} from "@/redux/actions/common.action";
import CatalogProductCarousel from "@/components/product/productCarousel/catalogProductCarousel";
import { createSelector } from "@reduxjs/toolkit";
import { LOADER_TYPES } from "@/constants";
import { isMobile } from "@/util";
import "./style.scss";

const TAB_NONE_STYLE = "none";
const TAB_DEFAULT_STYLE = "style1";

const NAVIGATION_TYPES = {
  INLINE: "inline",
  REDIRECTION: "redirectionUrl"
};

interface TabCSSProperties extends React.CSSProperties {
  tabBgColor?: string;
  tabBgSecondaryColor?: string;
  tabHgColor?: string;
  highlightedColor?: string;
  tabBorderColor?: string;
  fontColor?: string;
}

const DynamicTab: FC<DynamicTabProps> = ({ content }) => {
  const history = useHistory();
  const routeSlug = useRouteSlug();
  const countryId = useCountryId();
  const storeId = useAppSelector(state => state.page.homepageState.storeId);
  const language = useLongLanguage();
  const isRtl = useIsRTL();
  const [activeContent, setActiveContent] = useState(null);
  const [activeTab, setActiveTab] = useState<null | number>(null);
  const [productCarouselData, setProductCarouselData] = useState(null);
  const [carouselKey, setCarouselKey] = useState(0);
  const dispatch = useAppDispatch();

  const wrapperStyle = useMemo(() => {
    const styles: TabCSSProperties = { ...content.styles };
    if (content.width.value) {
      styles.width = content.width.value;
    }
    delete styles.backgroundColor;
    styles["--tabs-bg_color"] = content.styles.backgroundColor;

    styles["--tab-font_color"] = content.styles.fontColor;
    styles["--tab-bg_color"] = content.styles.tabBgColor;
    styles["--tab-bg-secondary-color"] = content.styles.tabBgSecondaryColor;
    styles["--tab-border-color"] = content.styles.tabBorderColor;

    styles["--tab-highlighted_font_color"] = content.styles.highlightedColor;
    styles["--tab-highlighted_bg_color"] = content.styles.tabHgColor;

    return styles;
  }, []);

  const showLoader = () =>
    dispatch(showPageLoader({ type: LOADER_TYPES.DOTS, fullscreen: true }));

  const hideLoader = () =>
    dispatch(hidePageLoader({ type: LOADER_TYPES.DOTS, fullscreen: true }));

  const handleSectionsData = async (sectionSlug: string) => {
    showLoader();

    try {
      const response = await SectionService.getDynamicSections({
        moduleType: SECTION_TABS_MODULE_TYPE,
        slug: sectionSlug
      });

      const dynamicData = response.find(
        data => data?.sectionSlug === sectionSlug
      );

      if (dynamicData?.content) {
        setActiveContent(dynamicData.content);
      } else {
        setActiveContent(null);
      }
    } catch (e) {
      console.error(e);
      setActiveContent(null);
    } finally {
      hideLoader();
    }
  };

  const handleCatalogData = async (catalogUrl: string) => {
    showLoader();

    const url = catalogUrl.startsWith("/") ? catalogUrl.slice(1) : catalogUrl;
    try {
      const response = await CatalogService.getPageData({
        url,
        countryId,
        language,
        storeId,
        limit: undefined,
        cookies: undefined
      });

      if (response?.data) {
        setActiveContent(response.data.content);
      } else {
        setActiveContent(null);
      }
    } catch (e) {
      console.error(e);
      setActiveContent(null);
    } finally {
      hideLoader();
    }
  };

  const onNavLinkClick = async (tab: any, isActive: boolean, index: number) => {
    const tabData = tab[language];
    if (isActive) return;
    setActiveTab(index);

    setProductCarouselData(null);
    if (tabData.navigationType === NAVIGATION_TYPES.REDIRECTION) {
      history.push(`/${routeSlug}${tabData.redirectionUrl}`);
    } else if (tabData.sectionSlug) {
      await handleSectionsData(tabData.sectionSlug);
    } else if (tabData.productListingUrl) {
      setCarouselKey(k => k + 1);
      setProductCarouselData(tab);
    } else if (tabData.navigationType === NAVIGATION_TYPES.INLINE) {
      await handleCatalogData(tabData.catalogUrl);
    }
  };

  // Initial setup
  useEffect(() => {
    if (!content?.tab) return;
    const defaultTabIndex = content.tab.findIndex(tab => tab.isDefault);
    setActiveTab(defaultTabIndex);
    const tabData = content.tab[defaultTabIndex][language];
    const tab = content.tab[defaultTabIndex];

    if (tabData.sectionSlug) {
      handleSectionsData(tabData.sectionSlug);
      //@ts-ignore
    } else if (tabData?.productListingUrl) {
      setCarouselKey(k => k + 1);
      setProductCarouselData(tab);
    } else if (tabData.catalogUrl) {
      handleCatalogData(tabData.catalogUrl);
    }
  }, [content, language]);

  if (!content?.tab) {
    console.error("No dynamicTab data found");
  }

  const tabStyle =
    content.tabStyle !== TAB_NONE_STYLE ? content.tabStyle : TAB_DEFAULT_STYLE;

  const isNotAvailableForResp =
    isMobile.any() && !content?.enabledOn?.includes("resp");

  if (isNotAvailableForResp) {
    return null;
  }

  const getImageList = createSelector(
    data =>
      data && Array.isArray(data.imageList) && shuffle([...data.imageList]),
    imageList => imageList || []
  );

  return (
    <>
      <div className={cn("dynamic_tab_wrapper", tabStyle)} style={wrapperStyle}>
        <Nav tabs>
          {content?.tab?.map((tab, index) => {
            const tabData = tab[language];
            const isActive = activeTab === index;
            const centralImage = tabData.imagePosition === "fullImage";
            let isIconLeft = tabData.imagePosition === "left";
            // Because of rtl we need to switch position
            if (isRtl) {
              isIconLeft = !isIconLeft;
            }

            return (
              <NavItem key={index}>
                <NavLink
                  className={cn({
                    active: isActive,
                    centralImage: centralImage
                  })}
                  onClick={() => onNavLinkClick(tab, isActive, index)}
                >
                  <span className="tabs_content">
                    {centralImage ? (
                      isActive ? (
                        <img
                          className={cn("fullimage_item")}
                          src={tabData.selected.imageURL}
                          alt="tab"
                        />
                      ) : (
                        <img
                          className={cn("fullimage_item")}
                          src={tabData.nonSelected.imageURL}
                          alt="tab"
                        />
                      )
                    ) : (
                      <>
                        {isIconLeft && tabData.imageURL && (
                          <img src={tabData.imageURL} alt="tab" />
                        )}
                        <span>{tabData.title}</span>
                        {!isIconLeft && tabData.imageURL && (
                          <img src={tabData.imageURL} alt="tab" />
                        )}
                      </>
                    )}
                  </span>
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>
        <TabContent>
          <TabPane>
            {productCarouselData ? (
              <CatalogProductCarousel
                key={carouselKey}
                getImageList={getImageList}
                content={productCarouselData}
                bannerModuleName={""}
                bannerPageTitle={""}
                isSaleCarousel={false}
              />
            ) : (
              !isEmpty(activeContent) &&
              activeContent.map(section => (
                <DynamicBannersWrapper section={section} key={section._id} />
              ))
            )}
          </TabPane>
        </TabContent>
      </div>
    </>
  );
};

export default DynamicTab;
